import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { propertySchema } from "@jornado/shared";
import { api, fehlertext } from "./api";
import { ChecklistenAmObjekt } from "./objektcheckliste";
import { ZahlenAmObjekt } from "./objektzahlen";
import { SollstundenAmObjekt } from "./leistungen";
import { PruefungenAmObjekt } from "./pruefungen";
import { ZugangAmObjekt } from "./zugang";
import { Wasseruebersicht } from "./wasser";
import type { KundeZeile } from "./kunden";
import {
  ArchivMarke, Auswahl, Eingabe, Feld, Fehler, Karte, Knopf, Laedt, Leer, Seitenkopf, Textfeld
} from "./ui";

interface ObjektZeile {
  id: string;
  name: string;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  municipality: string | null;
  service_level: string | null;
  archived_at: string | null;
  customer_id: string;
  customer_name: string;
  ohne_koordinate?: boolean;
}

interface ObjektVoll extends ObjektZeile {
  country: string | null;
  lat: string | null;
  lng: string | null;
  contract_start: string | null;
  contract_end: string | null;
  access_note: string | null;
  notes: string | null;
  has_pool_heating: boolean;
}

type Formular = Record<string, string>;

const LEER: Formular = {
  customerId: "", name: "", street: "", postalCode: "", city: "", municipality: "",
  country: "ES", lat: "", lng: "", serviceLevel: "", contractStart: "", contractEnd: "",
  // Reinigung ist der Regelfall im Bestand, deshalb steht der Haken bei einem
  // neuen Objekt von vornherein.
  accessNote: "", notes: "", poolheizung: ""
};

/** Datumsfelder liefert Postgres als Zeitstempel, das Formular braucht JJJJ-MM-TT. */
function alsDatum(wert: string | null): string {
  return wert ? String(wert).slice(0, 10) : "";
}

/** In der Anzeige gilt die Schreibweise der gewaehlten Sprache, nicht die der Datenbank. */
function alsAnzeigeDatum(wert: string | null, sprache: string): string | null {
  const iso = alsDatum(wert);
  return iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(sprache) : null;
}

// --------------------------------------------------------------------- Liste

export function ObjekteListe({ darfAendern }: { darfAendern: boolean }) {
  const { t } = useTranslation();
  const [objekte, setObjekte] = useState<ObjektZeile[] | null>(null);
  const [suche, setSuche] = useState("");
  const [mitArchiv, setMitArchiv] = useState(false);
  /*
   * Der Filter laesst sich auch von aussen setzen, etwa aus der
   * Einrichtungsliste auf der Uebersicht. Ohne diese Zeile fuehrte der Weg
   * dorthin auf die vollstaendige Objektliste, und der Nutzer haette den Filter
   * von Hand nachgestellt, ohne zu wissen, warum er hier steht.
   */
  const [params] = useSearchParams();
  const [nurOhneKoordinate, setNurOhneKoordinate] = useState(params.get("ohneKoordinate") === "ja");
  const [fehler, setFehler] = useState<string | null>(null);
  const [helfer, setHelfer] = useState(false);
  /** Zaehler, der ein Neuladen ausloest, wenn der Helfer etwas uebernommen hat. */
  const [stand, setStand] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      const p = new URLSearchParams();
      if (suche.trim()) p.set("q", suche.trim());
      if (mitArchiv) p.set("archiviert", "ja");
      if (nurOhneKoordinate) p.set("ohneKoordinate", "ja");
      api
        .get<{ properties: ObjektZeile[] }>(`/properties?${p}`)
        .then((r) => { setObjekte(r.properties); setFehler(null); })
        .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
    }, 250);
    return () => clearTimeout(timer);
  }, [suche, mitArchiv, nurOhneKoordinate, stand, t]);

  return (
    <>
      <Seitenkopf
        titel={t("objekte.titel")}
        hinweis={objekte ? t("objekte.anzahl", { count: objekte.length }) : undefined}
        aktionen={darfAendern && <Link to="/objekte/neu"><Knopf>{t("objekte.neu")}</Knopf></Link>}
      />

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <Eingabe
          type="search" value={suche} onChange={(e) => setSuche(e.target.value)}
          placeholder={t("objekte.suche")} aria-label={t("objekte.suche")}
        />
        <label className="flex shrink-0 items-center gap-2 text-sm text-leise">
          <input type="checkbox" checked={mitArchiv} onChange={(e) => setMitArchiv(e.target.checked)} />
          {t("archiv.zeigen")}
        </label>
        {/* Ohne Koordinate greift die Abstandspruefung am Objekt nicht. Der
            Filter macht aus einer unsichtbaren Luecke eine Arbeitsliste. */}
        <label className="flex shrink-0 items-center gap-2 text-sm text-leise">
          <input
            type="checkbox" checked={nurOhneKoordinate}
            onChange={(e) => setNurOhneKoordinate(e.target.checked)}
          />
          {t("objekte.nurOhneKoordinate")}
        </label>
      </div>

      <Fehler text={fehler} />

      {/*
        Der Reihe nach durch alle Objekte ohne Koordinate.
        Siebzehn Objekte einzeln aufzuschlagen, zu suchen und zurueckzugehen
        sind siebzehn Wege durch die Anwendung. Hier steht die Anschrift, der
        Vorschlag und der Knopf untereinander. Uebernommen wird trotzdem nur,
        was ein Mensch anklickt.
      */}
      {nurOhneKoordinate && objekte && objekte.length > 0 && (
        helfer
          ? (
            <Koordinatenhelfer
              objekte={objekte}
              onFertig={() => { setHelfer(false); setStand((x) => x + 1); }}
            />
          )
          : (
            <div className="mb-4">
              <Knopf art="neben" onClick={() => setHelfer(true)}>
                {t("objekte.helferStarten", { count: objekte.length })}
              </Knopf>
            </div>
          )
      )}

      {!objekte ? <Laedt /> : objekte.length === 0 ? <Leer text={t("objekte.leer")} /> : (
        <ul className="space-y-2">
          {objekte.map((o) => (
            <li key={o.id}>
              <Link to={`/objekte/${o.id}`} className="block rounded-card bg-white p-4 transition hover:bg-marke/10">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{o.name}</span>
                  <span className="text-sm text-leise">{o.customer_name}</span>
                  {o.archived_at && <ArchivMarke text={t("archiv.marke")} />}
                  {o.ohne_koordinate && (
                    <span className="rounded bg-marke/30 px-2 py-0.5 text-xs font-semibold">
                      {t("objekte.ohneKoordinate")}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm text-leise">
                  {[o.street, [o.postal_code, o.city].filter(Boolean).join(" "), o.municipality, o.service_level]
                    .filter(Boolean).join(" · ") || "—"}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/*
        * Der Wasserverlauf ueber alle Objekte.
        *
        * Er stand bis zum 04.09.2026 auf der Uebersicht und gehoert hierher:
        * es ist eine Aussage ueber alle Objekte, und von hier fuehrt jede
        * Zeile zu ihrem. Er steht unter der Liste und nicht darueber, denn wer
        * diese Seite oeffnet, sucht in aller Regel ein bestimmtes Objekt.
        */}
      {darfAendern && <Wasseruebersicht />}
    </>
  );
}

// -------------------------------------------------------------------- Detail

export function ObjektSeite({ darfAendern }: { darfAendern: boolean }) {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const [objekt, setObjekt] = useState<ObjektVoll | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bearbeiten, setBearbeiten] = useState(false);

  function laden() {
    api
      .get<{ property: ObjektVoll }>(`/properties/${id}`)
      .then((r) => { setObjekt(r.property); setFehler(null); })
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }
  useEffect(laden, [id]);

  async function archivUmschalten() {
    if (!objekt) return;
    try {
      await api.post(`/properties/${id}/${objekt.archived_at ? "restore" : "archive"}`);
      laden();
    } catch (e) {
      setFehler(fehlertext(e));
    }
  }

  if (fehler && !objekt) return <Fehler text={fehler} />;
  if (!objekt) return <Laedt />;

  if (bearbeiten) {
    return (
      <ObjektFormular
        titel={t("objekte.bearbeiten")}
        werte={ausObjekt(objekt)}
        onAbbruch={() => setBearbeiten(false)}
        onSpeichern={async (daten) => {
          await api.patch(`/properties/${id}`, daten);
          setBearbeiten(false);
          laden();
        }}
      />
    );
  }

  // Zielfuehrung in der offiziellen Adressform von Google Maps. Auf dem
  // Telefon uebernimmt die installierte Maps-App und startet die Navigation
  // ab dem aktuellen Standort, am Rechner oeffnet die Webkarte mit der Route.
  //
  // Liegen keine Koordinaten vor, wird die Anschrift als Ziel uebergeben. Das
  // ist ungenauer, aber besser als gar kein Weg, und auf Mallorca hat nicht
  // jede Finca eine Hausnummer.
  const ziel = objekt.lat && objekt.lng
    ? `${objekt.lat},${objekt.lng}`
    : [objekt.street, objekt.postal_code, objekt.city, objekt.country].filter(Boolean).join(", ");
  const route = ziel
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ziel)}`
    : null;

  return (
    <>
      <Seitenkopf
        titel={objekt.name}
        hinweis={objekt.customer_name}
        aktionen={darfAendern && (
          <>
            <Knopf art="neben" onClick={() => setBearbeiten(true)}>{t("aktion.bearbeiten")}</Knopf>
            <Knopf art="warnung" onClick={archivUmschalten}>
              {objekt.archived_at ? t("archiv.zurueckholen") : t("archiv.archivieren")}
            </Knopf>
          </>
        )}
      />
      <Fehler text={fehler} />
      {objekt.archived_at && <div className="mb-4"><ArchivMarke text={t("archiv.marke")} /></div>}

      <div className="grid gap-4 lg:grid-cols-2">
        <Karte>
          <h2 className="mb-3 font-bold">{t("objekte.lage")}</h2>
          <Zeile label={t("adresse.strasse")} wert={objekt.street} />
          <Zeile label={t("adresse.plz")} wert={objekt.postal_code} />
          <Zeile label={t("adresse.ort")} wert={objekt.city} />
          <Zeile label={t("objekte.gemeinde")} wert={objekt.municipality} />
          <Zeile label={t("adresse.land")} wert={objekt.country} />
          {route && (
            <a
              href={route}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-block rounded bg-marke px-4 py-2 text-sm font-semibold text-white transition hover:bg-marke-dunkel"
            >
              {t("objekte.route")}
            </a>
          )}

          {/* Ohne Koordinate kann die Anwendung nicht sagen, wie weit jemand
              beim Stempeln vom Objekt entfernt war. Der Sucher fuellt die
              Luecke, ohne dass jemand eine Karte aufmacht und Zahlen abtippt. */}
          {darfAendern && (!objekt.lat || !objekt.lng) && (
            <Koordinatensucher objekt={objekt} onUebernommen={laden} />
          )}
        </Karte>

        <Karte>
          <h2 className="mb-3 font-bold">{t("objekte.vertrag")}</h2>
          <Zeile label={t("objekte.leistung")} wert={objekt.service_level} />
          <Zeile label={t("objekte.beginn")} wert={alsAnzeigeDatum(objekt.contract_start, i18n.language)} />
          <Zeile label={t("objekte.ende")} wert={alsAnzeigeDatum(objekt.contract_end, i18n.language)} />
          <Zeile
            label={t("objekte.poolheizung")}
            wert={objekt.has_pool_heating ? t("aktion.ja") : t("aktion.nein")}
          />
          <Link to={`/kunden/${objekt.customer_id}`} className="mt-3 inline-block text-sm text-verweis underline">
            {t("objekte.zumKunden")}
          </Link>
        </Karte>

        <ZugangAmObjekt objektId={objekt.id} />

        {/* Was dieses Objekt je Woche braucht. Erst damit kann der Dienstplan
            sagen, ob eine Woche vollstaendig geplant ist. */}
        <SollstundenAmObjekt objektId={objekt.id} darfAendern={darfAendern} />

        <ChecklistenAmObjekt propertyId={objekt.id} darfAendern={darfAendern} />

        <PruefungenAmObjekt propertyId={objekt.id} />

        {/* Der Verlauf aus den Berichten: Wasser, Chlor, pH. Steht bewusst
            unter den Pruefungen, denn er ist deren Auswertung. Ohne Werte
            zeigt die Karte gar nichts, statt leer Platz zu belegen. */}
        <ZahlenAmObjekt propertyId={objekt.id} />

        {objekt.access_note && (
          <Karte className="lg:col-span-2">
            <h2 className="mb-3 font-bold">{t("objekte.anfahrt")}</h2>
            <p className="whitespace-pre-wrap text-sm">{objekt.access_note}</p>
          </Karte>
        )}
        {objekt.notes && (
          <Karte className="lg:col-span-2">
            <h2 className="mb-3 font-bold">{t("kunden.notizen")}</h2>
            <p className="whitespace-pre-wrap text-sm">{objekt.notes}</p>
          </Karte>
        )}
      </div>
    </>
  );
}

function Zeile({ label, wert }: { label: string; wert: string | null | undefined }) {
  return (
    <div className="flex gap-3 border-b border-linie py-1.5 last:border-0">
      <span className="w-40 shrink-0 text-sm text-leise">{label}</span>
      <span className="text-sm [overflow-wrap:anywhere]">{wert || "—"}</span>
    </div>
  );
}

// ------------------------------------------------------------------ Formular

export function ObjektNeu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const vorbelegt = params.get("kunde") ?? "";
  return (
    <ObjektFormular
      titel={t("objekte.neu")}
      werte={{ ...LEER, customerId: vorbelegt }}
      onAbbruch={() => navigate(vorbelegt ? `/kunden/${vorbelegt}` : "/objekte")}
      onSpeichern={async (daten) => {
        const r = await api.post<{ id: string }>("/properties", daten);
        navigate(`/objekte/${r.id}`);
      }}
    />
  );
}

function ausObjekt(o: ObjektVoll): Formular {
  return {
    customerId: o.customer_id, name: o.name, street: o.street ?? "",
    postalCode: o.postal_code ?? "", city: o.city ?? "", municipality: o.municipality ?? "",
    country: o.country ?? "", lat: o.lat ?? "", lng: o.lng ?? "",
    serviceLevel: o.service_level ?? "", contractStart: alsDatum(o.contract_start),
    contractEnd: alsDatum(o.contract_end), accessNote: o.access_note ?? "", notes: o.notes ?? "",
    // Als Text, weil das Formular ein Textspeicher ist. Vor dem Pruefen wird
    // daraus wieder ein Ja oder Nein.
    poolheizung: o.has_pool_heating ? "ja" : "",
  };
}

function ObjektFormular({
  titel, werte, onSpeichern, onAbbruch
}: {
  titel: string;
  werte: Formular;
  onSpeichern: (daten: unknown) => Promise<void>;
  onAbbruch: () => void;
}) {
  const { t } = useTranslation();
  const [f, setF] = useState<Formular>(werte);
  const [kunden, setKunden] = useState<KundeZeile[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const setzen = (k: string) => (e: { target: { value: string } }) =>
    setF((alt) => ({ ...alt, [k]: e.target.value }));

  useEffect(() => {
    api.get<{ customers: KundeZeile[] }>("/customers").then((r) => setKunden(r.customers)).catch(() => {});
  }, []);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    const geprueft = propertySchema.safeParse({
      ...f,
      poolheizung: f.poolheizung === "ja",
    });
    if (!geprueft.success) {
      setFehler(geprueft.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join(", "));
      return;
    }
    setLaeuft(true);
    try {
      await onSpeichern(geprueft.data);
    } catch (err) {
      setFehler(fehlertext(err, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <form onSubmit={absenden}>
      <Seitenkopf titel={titel} />
      <Fehler text={fehler} />
      <Karte>
        <div className="grid gap-4 sm:grid-cols-2">
          <Feld label={t("objekte.kunde")} breit>
            <Auswahl required value={f.customerId} onChange={setzen("customerId")}>
              <option value="">{t("objekte.kundeWaehlen")}</option>
              {kunden.map((k) => <option key={k.id} value={k.id}>{k.display_name}</option>)}
            </Auswahl>
          </Feld>
          <Feld label={t("objekte.name")} hinweis={t("objekte.nameHinweis")} breit>
            <Eingabe required value={f.name} onChange={setzen("name")} />
          </Feld>
          <Feld label={t("adresse.strasse")} breit>
            <Eingabe value={f.street} onChange={setzen("street")} />
          </Feld>
          <Feld label={t("adresse.plz")}>
            <Eingabe value={f.postalCode} onChange={setzen("postalCode")} />
          </Feld>
          <Feld label={t("adresse.ort")}>
            <Eingabe value={f.city} onChange={setzen("city")} />
          </Feld>
          <Feld label={t("objekte.gemeinde")} hinweis={t("objekte.gemeindeHinweis")}>
            <Eingabe value={f.municipality} onChange={setzen("municipality")} />
          </Feld>
          <Feld label={t("adresse.land")} hinweis={t("adresse.landHinweis")}>
            <Eingabe maxLength={2} value={f.country} onChange={setzen("country")} />
          </Feld>
          <Feld label={t("objekte.breite")} hinweis={t("objekte.koordinatenHinweis")}>
            <Eingabe inputMode="decimal" value={f.lat} onChange={setzen("lat")} />
          </Feld>
          <Feld label={t("objekte.laenge")}>
            <Eingabe inputMode="decimal" value={f.lng} onChange={setzen("lng")} />
          </Feld>
          <Feld label={t("objekte.leistung")} hinweis={t("objekte.leistungHinweis")}>
            <Eingabe value={f.serviceLevel} onChange={setzen("serviceLevel")} />
          </Feld>
          <Feld label={t("objekte.beginn")}>
            <Eingabe type="date" value={f.contractStart} onChange={setzen("contractStart")} />
          </Feld>
          <Feld label={t("objekte.ende")}>
            <Eingabe type="date" value={f.contractEnd} onChange={setzen("contractEnd")} />
          </Feld>
        </div>

        {/*
          Ausstattung, die der Mitarbeiter vor Ort kennen muss.

          Der Haken "Reinigung buchbar" stand hier daneben, solange der
          Eigentuemer im Portal eine Reinigung zu seinem Aufenthalt wuenschen
          konnte: nur was am Objekt stand, war ueberhaupt anzukreuzen. Portal
          und Aufenthalte sind entfernt, damit hat der Haken seinen einzigen
          Leser verloren.
        */}
        <div className="mt-8 space-y-3">
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={f.poolheizung === "ja"}
              onChange={(e) => setF((alt) => ({ ...alt, poolheizung: e.target.checked ? "ja" : "" }))}
              className="h-5 w-5 accent-marke"
            />
            <span>
              <span className="font-medium">{t("objekte.poolheizung")}</span>
              <span className="block text-xs text-leise">{t("objekte.poolheizungHinweis")}</span>
            </span>
          </label>
        </div>

        <div className="mt-8 grid gap-4">
          <Feld label={t("objekte.anfahrt")} hinweis={t("objekte.anfahrtHinweis")}>
            <Textfeld value={f.accessNote} onChange={setzen("accessNote")} />
          </Feld>
          <Feld label={t("kunden.notizen")}>
            <Textfeld value={f.notes} onChange={setzen("notes")} />
          </Feld>
        </div>

        <div className="mt-8 flex gap-3">
          <Knopf type="submit" disabled={laeuft}>{laeuft ? t("aktion.speichert") : t("aktion.speichern")}</Knopf>
          <Knopf type="button" art="neben" onClick={onAbbruch}>{t("aktion.abbrechen")}</Knopf>
        </div>
      </Karte>
    </form>
  );
}

/**
 * Koordinate zur Anschrift suchen.
 *
 * Die Anwendung schlaegt vor, uebernommen wird per Klick. Automatisch zu
 * speichern waere bequemer und falsch: eine Finca ohne Hausnummer findet jede
 * Suche irgendwo, und eine falsche Koordinate ist schlimmer als keine. Mit
 * keiner sagt die Abstandsrechnung ehrlich nichts, mit einer falschen sagt sie
 * still etwas Falsches.
 */
/**
 * Der Reihe nach durch alle Objekte ohne Koordinate.
 *
 * Der Kartendienst von OpenStreetMap ist kostenlos und bittet um hoechstens
 * eine Anfrage je Sekunde. Deshalb laeuft die Suche nacheinander mit einer
 * Sekunde Abstand und nicht siebzehnmal gleichzeitig. Siebzehn Objekte dauern
 * damit knapp zwanzig Sekunden, und in dieser Zeit fuellt sich die Liste von
 * oben nach unten.
 *
 * Uebernommen wird nichts von selbst. Eine automatisch gesetzte Koordinate
 * faellt niemandem auf, bis jemand die Karte oeffnet, und bis dahin rechnet
 * die Anwendung Abstaende gegen einen Punkt, den es so nicht gibt.
 */
function Koordinatenhelfer({
  objekte, onFertig
}: { objekte: ObjektZeile[]; onFertig: () => void }) {
  const { t } = useTranslation();
  const [gefunden, setGefunden] = useState<Record<string, Vorschlag[]>>({});
  const [geprueft, setGeprueft] = useState(0);
  const [uebernommen, setUebernommen] = useState<string[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    void (async () => {
      for (const o of objekte) {
        if (!aktiv) return;
        try {
          const a = await api.get<{ vorschlaege: Vorschlag[] }>(`/properties/${o.id}/koordinaten`);
          if (!aktiv) return;
          setGefunden((alt) => ({ ...alt, [o.id]: a.vorschlaege }));
        } catch {
          if (!aktiv) return;
          setGefunden((alt) => ({ ...alt, [o.id]: [] }));
        }
        setGeprueft((n) => n + 1);
        // Die Bitte des Dienstes: hoechstens eine Anfrage je Sekunde.
        await new Promise((f) => setTimeout(f, 1100));
      }
    })();
    return () => { aktiv = false; };
  }, [objekte]);

  async function uebernehmen(objektId: string, v: Vorschlag) {
    setFehler(null);
    try {
      await api.patch(`/properties/${objektId}`, { lat: v.lat, lng: v.lng });
      setUebernommen((alt) => [...alt, objektId]);
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <Karte className="mb-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-leise">
          {t("objekte.helferStand", { geprueft, gesamt: objekte.length })}
        </p>
        <Knopf art="neben" onClick={onFertig}>{t("objekte.helferBeenden")}</Knopf>
      </div>
      <Fehler text={fehler} />

      <ul className="mt-3 space-y-2">
        {objekte.map((o) => {
          const treffer = gefunden[o.id];
          const fertig = uebernommen.includes(o.id);
          return (
            <li key={o.id} className="rounded border border-linie p-3">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-semibold">{o.name}</span>
                <span className="text-sm text-leise">
                  {[o.street, o.postal_code, o.city].filter(Boolean).join(", ") || "—"}
                </span>
              </div>

              {fertig ? (
                <p className="mt-1 text-sm font-semibold text-verweis">
                  {t("objekte.helferUebernommen")}
                </p>
              ) : treffer === undefined ? (
                <p className="mt-1 text-sm text-leise">{t("objekte.helferWartet")}</p>
              ) : treffer.length === 0 ? (
                <p className="mt-1 text-sm text-leise">{t("objekte.koordinateNichts")}</p>
              ) : (
                <div className="mt-1">
                  <p className="text-sm">{treffer[0].beschreibung}</p>
                  <p className="font-mono text-xs text-leise">
                    {treffer[0].lat}, {treffer[0].lng}
                  </p>
                  <Guete v={treffer[0]} />
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Knopf onClick={() => void uebernehmen(o.id, treffer[0])}>
                      {t("objekte.koordinateUebernehmen")}
                    </Knopf>
                    <a
                      href={`https://www.openstreetmap.org/?mlat=${treffer[0].lat}&mlon=${treffer[0].lng}#map=17/${treffer[0].lat}/${treffer[0].lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded border border-linie/40 px-4 py-2 text-sm font-semibold transition hover:bg-grund"
                    >
                      {t("objekte.koordinateAnsehen")}
                    </a>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Karte>
  );
}

function Koordinatensucher({
  objekt, onUebernommen
}: { objekt: ObjektVoll; onUebernommen: () => void }) {
  const { t } = useTranslation();
  const [vorschlaege, setVorschlaege] = useState<Vorschlag[] | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function suchen() {
    setLaeuft(true);
    setFehler(null);
    try {
      const a = await api.get<{ vorschlaege: Vorschlag[] }>(`/properties/${objekt.id}/koordinaten`);
      setVorschlaege(a.vorschlaege);
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.laden")));
    } finally {
      setLaeuft(false);
    }
  }

  async function uebernehmen(v: Vorschlag) {
    setLaeuft(true);
    try {
      await api.patch(`/properties/${objekt.id}`, { lat: v.lat, lng: v.lng });
      onUebernommen();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
      setLaeuft(false);
    }
  }

  return (
    <div className="mt-4 border-t border-linie pt-4">
      <p className="text-sm text-leise">{t("objekte.koordinateFehlt")}</p>
      <div className="mt-2">
        <Knopf art="neben" disabled={laeuft} onClick={() => void suchen()}>
          {t("objekte.koordinateSuchen")}
        </Knopf>
      </div>
      <Fehler text={fehler} />

      {vorschlaege !== null && (
        vorschlaege.length === 0 ? (
          <p className="mt-3 text-sm text-leise">{t("objekte.koordinateNichts")}</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {vorschlaege.map((v) => (
              <li key={`${v.lat},${v.lng}`} className="rounded border border-linie p-3">
                <p className="text-sm">{v.beschreibung}</p>
                <p className="mt-1 font-mono text-xs text-leise">{v.lat}, {v.lng}</p>
                <Guete v={v} />
                <div className="mt-2 flex flex-wrap gap-2">
                  <Knopf disabled={laeuft} onClick={() => void uebernehmen(v)}>
                    {t("objekte.koordinateUebernehmen")}
                  </Knopf>
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${v.lat}&mlon=${v.lng}#map=17/${v.lat}/${v.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded border border-linie/40 px-4 py-2 text-sm font-semibold transition hover:bg-grund"
                  >
                    {t("objekte.koordinateAnsehen")}
                  </a>
                </div>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}

interface Vorschlag {
  lat: number;
  lng: number;
  beschreibung: string;
  /** "adresse" mit Hausnummer, "strasse" irgendwo an der Strasse. */
  genauigkeit?: "adresse" | "strasse";
}

/**
 * Der Hinweis auf einen Treffer ohne Hausnummer.
 *
 * OpenStreetMap kennt auf Mallorca viele Strassen, aber laengst nicht jede
 * Hausnummer, und eine Finca hat oft gar keine. Findet die Suche die Anschrift
 * nicht, fragt sie die Strasse nach. Fuer die Frage, ob jemand am Objekt
 * gestempelt hat, reicht das, fuer eine Wegbeschreibung zur Haustuer nicht.
 * Deshalb steht es dran, statt dass der Punkt so aussieht wie ein Volltreffer.
 */
function Guete({ v }: { v: Vorschlag }) {
  const { t } = useTranslation();
  if (v.genauigkeit !== "strasse") return null;
  return (
    <p className="mt-1 text-xs font-semibold text-verweis">{t("objekte.nurStrasse")}</p>
  );
}
